"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface UseVideoLoadingProps {
	src: string;
	autoPlay?: boolean;
	muted?: boolean;
	loop?: boolean;
	playsInline?: boolean;
	preload?: "none" | "metadata" | "auto";
}

export function useVideoLoading({
	src,
	autoPlay = true,
	muted = true,
	loop = true,
	playsInline = true,
	preload = "auto",
}: UseVideoLoadingProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const autoplayBlockedRef = useRef(false);
	const [isLoaded, setIsLoaded] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [isAutoplayBlocked, setIsAutoplayBlocked] = useState(false);

	const applyPlaybackAttributes = useCallback(
		(video: HTMLVideoElement) => {
			video.muted = muted;
			video.defaultMuted = muted;
			video.loop = loop;
			video.playsInline = playsInline;

			if (playsInline) {
				// Improves compatibility with iOS Safari autoplay behavior.
				video.setAttribute("playsinline", "true");
				video.setAttribute("webkit-playsinline", "true");
			}
		},
		[loop, muted, playsInline],
	);

	const retryPlay = useCallback(async () => {
		const video = videoRef.current;
		if (!video || !autoPlay || !src) return false;

		applyPlaybackAttributes(video);

		try {
			await video.play();
			autoplayBlockedRef.current = false;
			setIsAutoplayBlocked(false);
			setHasError(false);
			return true;
		} catch (error) {
			const errorName =
				error instanceof DOMException ? error.name : (error as { name?: string } | null)?.name;
			const isBlocked = errorName === "NotAllowedError";
			const isTransientAbort = errorName === "AbortError";

			if (isBlocked) {
				autoplayBlockedRef.current = true;
				setIsAutoplayBlocked(true);
				return false;
			}

			if (isTransientAbort) {
				return false;
			}

			setHasError(true);
			return false;
		}
	}, [applyPlaybackAttributes, autoPlay, src]);

	useEffect(() => {
		const video = videoRef.current;
		if (!video || !src) {
			autoplayBlockedRef.current = false;
			setIsLoaded(false);
			setIsPlaying(false);
			setHasError(false);
			setIsAutoplayBlocked(false);
			return;
		}

		// Reset states when src changes
		setIsLoaded(false);
		setIsPlaying(false);
		setHasError(false);
		autoplayBlockedRef.current = false;
		setIsAutoplayBlocked(false);
		applyPlaybackAttributes(video);

		const handleLoadedData = () => {
			setIsLoaded(true);
			setHasError(false);
		};

		const handleCanPlay = () => {
			setIsLoaded(true);
			void retryPlay();
		};

		const handlePlay = () => {
			setIsPlaying(true);
			autoplayBlockedRef.current = false;
			setIsAutoplayBlocked(false);
		};

		const handlePause = () => {
			setIsPlaying(false);
		};

		const handleError = () => {
			setHasError(true);
			setIsLoaded(false);
		};

		const handleVisibilityChange = () => {
			if (!document.hidden && autoPlay) {
				void retryPlay();
			}
		};

		const handleUserGesture = () => {
			if (autoplayBlockedRef.current) {
				void retryPlay();
			}
		};

		video.addEventListener("loadeddata", handleLoadedData);
		video.addEventListener("loadedmetadata", handleLoadedData);
		video.addEventListener("canplay", handleCanPlay);
		video.addEventListener("canplaythrough", handleCanPlay);
		video.addEventListener("play", handlePlay);
		video.addEventListener("playing", handlePlay);
		video.addEventListener("pause", handlePause);
		video.addEventListener("error", handleError);
		document.addEventListener("visibilitychange", handleVisibilityChange);
		document.addEventListener("pointerdown", handleUserGesture);
		document.addEventListener("touchstart", handleUserGesture);
		document.addEventListener("keydown", handleUserGesture);

		if (autoPlay) {
			void retryPlay();
		}

		return () => {
			video.removeEventListener("loadeddata", handleLoadedData);
			video.removeEventListener("loadedmetadata", handleLoadedData);
			video.removeEventListener("canplay", handleCanPlay);
			video.removeEventListener("canplaythrough", handleCanPlay);
			video.removeEventListener("play", handlePlay);
			video.removeEventListener("playing", handlePlay);
			video.removeEventListener("pause", handlePause);
			video.removeEventListener("error", handleError);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			document.removeEventListener("pointerdown", handleUserGesture);
			document.removeEventListener("touchstart", handleUserGesture);
			document.removeEventListener("keydown", handleUserGesture);
		};
	}, [applyPlaybackAttributes, autoPlay, retryPlay, src]);

	const videoProps = {
		ref: videoRef,
		autoPlay,
		muted,
		defaultMuted: muted,
		loop,
		playsInline,
		preload,
		style: {
			opacity: (isLoaded || isPlaying) && !hasError ? 1 : 0,
			transition: "opacity 1s ease-in-out",
		},
	};

	return {
		videoRef,
		videoProps,
		isLoaded,
		isPlaying,
		hasError,
		isAutoplayBlocked,
		retryPlay,
	};
}
